import { type BlockConfig, iconField } from './_types';

/**
 * Payload `Block` config sidecar for the `splitContent` block.
 *
 * Field tree mirrors the props/data the React component (`./split-content.tsx`)
 * needs to render a 2-column section with text + bullets on one side and a
 * visual (lucide icon in a gradient square OR a real image) on the other.
 * Decomposed from the production Partners "What Sets Reklaim Apart" pattern
 * per REK-1587 Phase-8a-fix.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `split-content.tsx` / `split-content.payload.ts`
 *   React component export:  `SplitContent`
 *   Payload `slug`:          `splitContent`
 *   Payload `interfaceName`: `SplitContentBlock`
 */

export const SplitContentBlock: BlockConfig = {
  slug: 'splitContent',
  interfaceName: 'SplitContentBlock',
  labels: {
    singular: 'Split Content',
    plural: 'Split Content Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow / Badge',
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline / Lead paragraph',
      admin: { rows: 3 },
    },
    {
      name: 'items',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 12,
      label: 'Bullet items',
      labels: { singular: 'Item', plural: 'Items' },
      fields: [
        {
          ...iconField({
            name: 'icon',
            label: 'Icon (lucide-react name)',
          }),
          admin: {
            description:
              'Optional PascalCase lucide-react icon name. Defaults to CheckCircle2 when omitted. Supported icons (matching the component allow-list): Award, BadgeCheck, BarChart3, Bell, Bot, Building2, Calendar, CheckCircle2, Compass, Database, Eye, FileCheck, Gift, Globe, Heart, Lock, Mail, MessageCircle, Rocket, Search, Settings, Share2, Shield, Smartphone, Sparkles, Star, Target, TrendingUp, Trophy, User, Users, Wallet, Zap. Unknown names fall back to Sparkles.',
          },
        },
        {
          name: 'title',
          type: 'text',
          required: true,
          label: 'Title (bullet text)',
        },
        {
          name: 'description',
          type: 'textarea',
          label: 'Optional description',
          admin: { rows: 2 },
        },
      ],
    },
    {
      name: 'visual',
      type: 'group',
      required: true,
      label: 'Visual (icon OR image)',
      admin: {
        description:
          'Either `icon` OR `image` should be supplied. If both are omitted, the React component renders a default `Sparkles` icon so the 2-column composition doesn\'t end up with an empty right-hand column (defensive fallback added in REK-1587). At cutover this should be enforced via a Payload `validate` callback on the group ("icon or image required") so the CMS catches incomplete saves at authoring time.',
      },
      fields: [
        {
          ...iconField({
            name: 'icon',
            label: 'Lucide icon name (PascalCase)',
          }),
          admin: {
            description:
              'When set (and no image is supplied), renders the lucide icon in a gradient-tinted square. Used by the production Partners "What Sets Apart" Globe visual. Omitting both this and `image` falls back to the Sparkles default.',
          },
        },
        {
          name: 'image',
          type: 'group',
          label: 'Image asset (overrides icon when set)',
          admin: {
            description:
              'OPTIONAL. To use an icon instead, leave `src` + `alt` blank and supply `visual.icon` above. WHEN you do use an image, both `src` AND `alt` must be filled -- the React component renders `<BlockImage src alt />` directly. The "icon-or-image, pick one" expectation should be enforced at cutover via a Payload group-level `validate` callback (`if (image.src && !image.alt) return "alt required"`). Until then, this is an authoring convention.',
          },
          fields: [
            // src + alt are intentionally optional at the Payload
            // schema level so a CMS author can use the icon path
            // without being forced to fill in image.src + image.alt
            // (a `required: true` here would force the image path
            // for every save, blocking the icon-only path that
            // /partners + the test fallback rely on). At cutover, a
            // group-level `validate` callback should enforce "if src
            // set, alt must also be set" -- but the React component
            // already handles undefined values gracefully by only
            // rendering the image branch when `visual.image` is set.
            // Relaxed back from required:true per Copilot R4
            // review.
            { name: 'src', type: 'text', label: 'Image URL' },
            { name: 'alt', type: 'text', label: 'Alt text' },
          ],
        },
      ],
    },
    {
      name: 'imagePosition',
      type: 'select',
      defaultValue: 'right',
      label: 'Visual position',
      options: [
        { label: 'Right (text LEFT, visual RIGHT)', value: 'right' },
        { label: 'Left (visual LEFT, text RIGHT)', value: 'left' },
      ],
    },
    {
      name: 'bgVariant',
      type: 'select',
      defaultValue: 'default',
      label: 'Background variant',
      options: [
        { label: 'Default', value: 'default' },
        { label: 'Muted', value: 'muted' },
      ],
    },
  ],
};

export default SplitContentBlock;
